import React, { useState, useRef, useCallback, useEffect, useLayoutEffect } from 'react';
import {
  ArrowRight01Icon,
  BubbleChatQuestionIcon,
  ScissorIcon,
  SentIcon,
  SmileIcon,
  SparklesIcon,
  TextIcon,
} from "hugeicons-react"
import "../../Styles/Layout/TextToolBar.css"

export type TextSelectionAction = 'explain' | 'improve' | 'shorten' | 'tone' | 'grammar' | 'custom';

interface TextToolBarProps {
  optionsPosition: {
    top: number;
    left: number;
  };
  contentContainerRef: React.RefObject<HTMLDivElement | HTMLElement | null>;
  handleMouseUp: (event: MouseEvent) => void;
  onAction: (
    e: React.SyntheticEvent,
    action: TextSelectionAction,
    customPrompt?: string,
  ) => void;
}

export const TextToolBar: React.FC<TextToolBarProps> = ({ 
  optionsPosition, 
  contentContainerRef, 
  handleMouseUp, 
  onAction,
}) => {
    const [prompt, setPrompt] = useState('');
    const [expanded, setExpanded] = useState(false);
    const [typingWidth, setTypingWidth] = useState<number | null>(null);
    const optionsRef = useRef<HTMLDivElement>(null);
    const contentRef = useRef<HTMLDivElement>(null);
    const [toolbarPosition, setToolbarPosition] = useState(optionsPosition);
    const hasPrompt = prompt.trim().length > 0;

    const clampToolbar = useCallback(() => {
      const contentEl = contentContainerRef.current;
      const panel = contentEl?.parentElement;
      if (!contentEl || !panel) return;

      const panelRect = panel.getBoundingClientRect();
      const toolbarWidth = Math.min(optionsRef.current?.getBoundingClientRect().width || 360, panelRect.width - 20);
      const half = toolbarWidth / 2;
      const margin = 10;

      let newLeft = optionsPosition.left;
      if (panelRect.width <= toolbarWidth + 2 * margin) {
        newLeft = panelRect.width / 2;
      } else {
        newLeft = Math.min(
          Math.max(newLeft, half + margin),
          panelRect.width - half - margin
        );
      }

      setToolbarPosition({
        top: optionsPosition.top,
        left: newLeft
      });
    }, [optionsPosition, contentContainerRef]);

    useLayoutEffect(clampToolbar, [expanded, hasPrompt, typingWidth, clampToolbar]);

    useEffect(() => {
      const toolbar = optionsRef.current;
      if (!toolbar) return;
      const observer = new ResizeObserver(clampToolbar);
      observer.observe(toolbar);
      window.addEventListener('resize', clampToolbar);
      return () => {
        observer.disconnect();
        window.removeEventListener('resize', clampToolbar);
      };
    }, [clampToolbar]);

    const handleClickOutside = useCallback((event: MouseEvent) => {
        const insideContent = contentContainerRef.current?.contains(event.target as Node);
        const insideOptions = optionsRef.current?.contains(event.target as Node);

        if (!insideContent && !insideOptions) {
            setExpanded(false);
            setPrompt('');
            setTypingWidth(null);
        }
    }, [contentContainerRef]);

    useEffect(() => {
        document.addEventListener('mouseup', handleMouseUp);
        document.addEventListener('mousedown', handleClickOutside);
        
        return () => {
            document.removeEventListener('mouseup', handleMouseUp);
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [handleClickOutside, handleMouseUp]);

    const runAction = (e: React.MouseEvent, action: TextSelectionAction) => {
      e.stopPropagation();
      onAction(e, action);
    };

    const submitPrompt = (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      if (!prompt.trim()) return;
      onAction(e, 'custom', prompt.trim());
    };

    const handlePromptChange = (nextPrompt: string) => {
      if (!prompt.trim() && nextPrompt.trim()) {
        setTypingWidth(Math.ceil(optionsRef.current?.getBoundingClientRect().width || 0));
      } else if (!nextPrompt.trim()) {
        setTypingWidth(null);
      }
      setPrompt(nextPrompt);
    };

    const iconProps = { size: 14, strokeWidth: 1.8, 'aria-hidden': true } as const;
    const actionTabIndex = hasPrompt ? -1 : 0;

    return (
        <div
            className="text-toolbar"
            style={{
                top: `${toolbarPosition.top}px`,
                left: `${toolbarPosition.left}px`,
                width: hasPrompt && typingWidth ? `${typingWidth}px` : undefined,
            }}
            onMouseDown={(e) => e.stopPropagation()}
            onMouseUp={(e) => e.stopPropagation()}
            ref={optionsRef}
        >
            <div
              className="text-toolbar-content"
              ref={contentRef}
              style={{ width: hasPrompt && typingWidth ? `${typingWidth - 8}px` : undefined }}
            >
                <div
                  className="text-toolbar-prompt-clip"
                  style={{
                    maxWidth: expanded ? 0 : hasPrompt && typingWidth ? `${typingWidth - 40}px` : '145px',
                    opacity: expanded ? 0 : 1,
                    transform: expanded ? 'translateX(-8px)' : 'translateX(0)',
                  }}
                >
                  <form
                    className="text-toolbar-prompt"
                    onSubmit={submitPrompt}
                    style={{ width: hasPrompt && typingWidth ? `${typingWidth - 40}px` : '145px' }}
                  >
                      <input
                          type="text"
                          value={prompt}
                          onChange={(e) => handlePromptChange(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Escape') {
                              setPrompt('');
                              setExpanded(false);
                              setTypingWidth(null);
                            }
                          }}
                          tabIndex={expanded ? -1 : 0}
                          aria-label="Describe edits"
                          placeholder="Describe edits"
                          className="text-toolbar-prompt-input"
                      />
                  </form>
                </div>

                <div
                  className="text-toolbar-actions"
                  aria-hidden={hasPrompt}
                  style={{
                    maxWidth: hasPrompt ? 0 : expanded ? '462px' : '224px',
                    opacity: hasPrompt ? 0 : 1,
                    transform: hasPrompt ? 'translateX(-8px)' : 'translateX(0)',
                    pointerEvents: hasPrompt ? 'none' : 'auto',
                  }}
                >
                    {!expanded && <span className="text-toolbar-separator text-toolbar-leading-separator" />}
                    <button type="button" tabIndex={actionTabIndex} className="text-toolbar-button" onClick={(e) => runAction(e, 'explain')}>
                      <BubbleChatQuestionIcon {...iconProps} />
                      <span>Explain</span>
                    </button>
                    <button type="button" tabIndex={actionTabIndex} className="text-toolbar-button" onClick={(e) => runAction(e, 'improve')}>
                      <SparklesIcon {...iconProps} />
                      <span>Improve</span>
                    </button>
                    <div className={`text-toolbar-more${expanded ? ' is-expanded' : ''}`}>
                      <button type="button" tabIndex={hasPrompt || !expanded ? -1 : 0} className="text-toolbar-button" onClick={(e) => runAction(e, 'shorten')}>
                        <ScissorIcon {...iconProps} />
                        <span>Shorten</span>
                      </button>
                      <button type="button" tabIndex={hasPrompt || !expanded ? -1 : 0} className="text-toolbar-button" onClick={(e) => runAction(e, 'tone')}>
                        <SmileIcon {...iconProps} />
                        <span>Tone</span>
                      </button>
                      <button type="button" tabIndex={hasPrompt || !expanded ? -1 : 0} className="text-toolbar-button" onClick={(e) => runAction(e, 'grammar')}>
                        <TextIcon {...iconProps} />
                        <span>Grammar</span>
                      </button>
                    </div>
                    <span className="text-toolbar-separator" />
                    <button
                      type="button"
                      tabIndex={actionTabIndex}
                      className="text-toolbar-expand"
                      aria-label={expanded ? 'Show fewer actions' : 'Show more actions'}
                      aria-expanded={expanded}
                      onClick={() => setExpanded((value) => !value)}
                    >
                      <ArrowRight01Icon size={15} className={expanded ? 'is-expanded' : ''} />
                    </button>
                </div>

                <div
                  className="text-toolbar-send-clip"
                  aria-hidden={!hasPrompt}
                  style={{
                    maxWidth: hasPrompt ? '30px' : 0,
                    opacity: hasPrompt ? 1 : 0,
                    transform: hasPrompt ? 'scale(1)' : 'scale(0.88)',
                    pointerEvents: hasPrompt ? 'auto' : 'none',
                  }}
                >
                  <button
                    type="button"
                    tabIndex={hasPrompt ? 0 : -1}
                    aria-label="Send edit instruction"
                    className="text-toolbar-send"
                    onClick={(e) => {
                      e.stopPropagation();
                      onAction(e, 'custom', prompt.trim());
                    }}
                  >
                    <SentIcon size={15} />
                  </button>
                </div>
            </div>
        </div>
    );
}
